import React, { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useConfirmOptional } from '../hooks/useConfirm';
import { useUnsavedChangesCopy } from '../hooks/useUnsavedChanges';
import { useModalToastAnchor } from '../contexts/ToastContext';
import { Icon } from './Icon';

/**
 * Parse the numeric tier from a Tailwind z-index class so error toasts can pick
 * the top-most modal. `z-[10000]` → 10000, `z-50` → 50, anything else → 50.
 */
function parseZTier(zIndex: string): number {
    const bracket = zIndex.match(/z-\[(\d+)\]/);
    if (bracket) return Number(bracket[1]);
    const named = zIndex.match(/z-(\d+)/);
    if (named) return Number(named[1]);
    return 50;
}

interface ModalShellProps {
    isOpen: boolean;
    onClose: () => void;
    /**
     * When `true`, closing the modal (X button or Escape) first asks the user
     * to confirm via the shared dialog, so unsaved edits aren't lost. Pass the
     * form's `isDirty` (e.g. from `useDirtyTracker`). Requires a
     * `<ConfirmProvider>` ancestor; degrades to a plain close if absent.
     */
    dirty?: boolean;
    title: string;
    subtitle?: string;
    /** Renders inline before the title (e.g. status badge). */
    titleInlineStart?: React.ReactNode;
    /** Renders inline after the title. */
    titleInlineEnd?: React.ReactNode;
    /** Optional content between title block and header actions. */
    headerMeta?: React.ReactNode;
    maxWidth?: string;
    zIndex?: string;
    headerActions?: React.ReactNode;
    children: React.ReactNode;
    /**
     * Vertical sizing strategy:
     * - `'full'` (default): takes `h-[90vh]` on mobile so content can scroll
     *   inside a large modal (DesignModal, BundleModal, OrderEditor, etc.).
     * - `'auto'`: grows with its content, capped at `max-h-[90vh]`. Use for
     *   short dialogs like `ConfirmDialog` where a fixed height looks empty.
     */
    size?: 'full' | 'auto';
    /** aria-label for the close (X) button. Default Spanish ("Cerrar modal"). */
    closeAriaLabel?: string;
    /**
     * When `false`, the body drops its default `p-6` padding and single scroll
     * area, becoming a flex column that lets the child own the layout and its
     * own scrolling (e.g. a two-pane settings modal with a sticky sidebar).
     * Defaults to `true` (unchanged behaviour for every existing modal).
     */
    contentPadding?: boolean;
}

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** First element worth landing on when the modal opens. The visually hidden
 *  `<input class="sr-only">` of a `Checkbox` matches the selector and used to
 *  take the autofocus, so the modal opened with focus "nowhere". */
function firstAutofocusTarget(root: HTMLElement): HTMLElement | undefined {
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).find(
        (el) => !el.classList.contains('sr-only') && el.getAttribute('aria-hidden') !== 'true',
    );
}

const ModalShell: React.FC<ModalShellProps> = ({
    isOpen,
    onClose,
    dirty,
    title,
    subtitle,
    titleInlineStart,
    titleInlineEnd,
    headerMeta,
    maxWidth = 'max-w-2xl',
    zIndex = 'z-50',
    headerActions,
    children,
    size = 'full',
    closeAriaLabel = 'Cerrar modal',
    contentPadding = true,
}) => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);
    // Unique per instance: two stacked modals used to share `id="modal-title"`.
    const titleId = useId();
    const subtitleId = `${titleId}-subtitle`;
    const confirm = useConfirmOptional();
    const copy = useUnsavedChangesCopy();

    // Anchor for in-modal error/warning toasts (rendered as a band between the
    // header and body). Registering with the shared ToastProvider lets it portal
    // errors into the top-most open modal. Unregisters on close/unmount.
    const anchorRef = useRef<HTMLDivElement>(null);
    const registerAnchor = useModalToastAnchor();
    const zTier = parseZTier(zIndex);
    useEffect(() => {
        if (!isOpen || !registerAnchor || !anchorRef.current) return;
        return registerAnchor(anchorRef.current, zTier);
    }, [isOpen, registerAnchor, zTier]);

    // Close path: confirm first when there are unsaved changes. When no confirm
    // provider is available (or not dirty), close immediately.
    const requestClose = useCallback(() => {
        if (dirty && confirm) {
            void confirm({
                title: copy.title,
                message: copy.message,
                confirmLabel: copy.confirmLabel,
                variant: 'warning',
            }).then((ok) => {
                if (ok) onClose();
            });
            return;
        }
        onClose();
    }, [dirty, confirm, copy, onClose]);

    // Ref to the latest requestClose so the key handler doesn't need it as a
    // dependency (avoids re-running the effect and stealing focus on every
    // parent re-render).
    const requestCloseRef = useRef(requestClose);
    useEffect(() => { requestCloseRef.current = requestClose; }, [requestClose]);

    useEffect(() => {
        if (!isOpen) return;
        previousFocusRef.current = document.activeElement as HTMLElement;

        const keyHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                requestCloseRef.current();
                return;
            }
            if (e.key === 'Tab' && dialogRef.current) {
                const focusable = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
                if (focusable.length === 0) return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last?.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first?.focus();
                }
            }
        };

        document.addEventListener('keydown', keyHandler);
        const timer = requestAnimationFrame(() => {
            const firstFocusable = dialogRef.current ? firstAutofocusTarget(dialogRef.current) : undefined;
            firstFocusable?.focus();
        });
        return () => {
            document.removeEventListener('keydown', keyHandler);
            cancelAnimationFrame(timer);
            previousFocusRef.current?.focus();
        };
    }, [isOpen]);

    if (!isOpen) return null;

    // Portal to <body> so the modal escapes any ancestor stacking context. A
    // `position: fixed` overlay rendered deep inside a transformed/animated
    // subtree (e.g. a tab with `animate-in`) gets trapped in that context and
    // page content (floating field labels) can bleed over it; portalling fixes
    // it app-wide and keeps the zIndex prop authoritative for layering.
    return createPortal(
        <div
            className={`fixed inset-0 ${zIndex} flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={subtitle ? subtitleId : undefined}
        >
            <div
                ref={dialogRef}
                className={`bg-surface-light dark:bg-surface-dark rounded-xl w-full ${maxWidth} flex flex-col shadow-2xl ${size === 'auto' ? 'h-auto max-h-[90vh]' : 'h-[90vh] md:h-auto md:max-h-[90vh]'} border border-border-subtle dark:border-border-subtle-dark animate-in fade-in zoom-in-95 duration-200 my-auto`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Two columns: what may wrap (title, meta, actions) and the
                    close «×», which never wraps and stays top-right. With the
                    «×» inside the wrapping row it dropped to the actions' line
                    on a phone and, with wide actions (a segmented control +
                    «Guardar», longer words in pl/de), ended up past the
                    panel's edge.
                    - From `md` up the left column is ONE wrapping flex row, so
                      a header that fits stays one line, as always (`items-start`
                      + the row's `min-h-10`, the «×»'s 40 px, keep it centred).
                    - Below `md` that row is `display: contents`: title and «×»
                      share the first line and meta + actions get lines of
                      their own across the WHOLE width, right-aligned and
                      wrapping, instead of the width the «×» leaves. */}
                <header className="px-6 py-4 border-b border-border-subtle dark:border-border-subtle-dark grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 shrink-0 bg-white dark:bg-surface-dark rounded-t-xl">
                    <div className="max-md:contents md:min-w-0 md:min-h-10 md:flex md:flex-wrap md:items-center md:gap-3 md:justify-between">
                        {/* A real basis (10rem), not `flex-1`'s 0: with a basis of 0
                            the header never wrapped, so the title block shrank to
                            whatever the actions left (~110 px) and `break-words`
                            split a word in two («RECURRENT / ES»). Now the actions
                            drop to their own line (right-aligned by `ml-auto`)
                            before the title gets narrower than that. */}
                        <div className="min-w-0 pr-2 self-center md:flex-[1_1_10rem]">
                            {/* Outfit's word space is narrow, and uppercase +
                                `tracking-tight` made it vanish («PRUEBARECURRENTE»):
                                the space gets a little back. */}
                            <h2
                                id={titleId}
                                className="inline-flex max-w-full min-w-0 items-center gap-1 text-lg font-bold text-text-main dark:text-white tracking-tight [word-spacing:0.12em] uppercase leading-tight"
                            >
                                {titleInlineStart != null ? (
                                    <span className="shrink-0 inline-flex items-center leading-none">{titleInlineStart}</span>
                                ) : null}
                                {/* `break-words` instead of `truncate` so titles in narrow
                                    modals (e.g. ConfirmDialog at max-w-md) wrap to a
                                    second line when the header actions would otherwise
                                    squeeze them — important for translated copy where
                                    the same key is longer in some languages. */}
                                <span className="min-w-0 flex-1 break-words hyphens-auto">{title}</span>
                                {titleInlineEnd != null ? (
                                    <span className="shrink-0 inline-flex items-center leading-none">{titleInlineEnd}</span>
                                ) : null}
                            </h2>
                            {subtitle && <p id={subtitleId} className="text-xs font-medium text-text-sub mt-1">{subtitle}</p>}
                        </div>
                        {headerMeta != null ? (
                            <div className="flex items-center gap-2 shrink-0 max-w-full max-md:col-span-2 max-md:justify-self-end">{headerMeta}</div>
                        ) : null}
                        {/* The actions wrap too, right-aligned, and so does the plain
                            `<div className="flex items-center gap-2">` most modals
                            wrap their buttons in (but not a `role` group such as a
                            segmented control, which stays one pill). */}
                        {headerActions != null && headerActions !== false ? (
                            <div className="flex flex-wrap items-center justify-end gap-3 min-w-0 max-w-full max-md:col-span-2 max-md:justify-self-end md:ml-auto [&>div:not([role])]:flex-wrap [&>div:not([role])]:justify-end [&>div:not([role])]:max-w-full">
                                {headerActions}
                            </div>
                        ) : null}
                    </div>
                    <button type="button" onClick={requestClose} className="col-start-2 row-start-1 p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800/50 text-slate-500 transition-colors" aria-label={closeAriaLabel}>
                        <Icon name="close" aria-hidden="true" />
                    </button>
                </header>
                {/* In-modal error/warning toasts portal here (kept outside the
                    scroll area so they stay visible under the action button).
                    `empty:hidden` → zero layout impact when there's no error. */}
                <div ref={anchorRef} className="empty:hidden shrink-0 px-6 pt-4 flex flex-col gap-2 bg-slate-50/50 dark:bg-background-dark" />
                <main className={`flex-1 min-h-0 bg-slate-50/50 dark:bg-background-dark rounded-b-xl ${contentPadding ? 'overflow-y-auto p-6' : 'flex flex-col overflow-hidden'}`}>
                    {children}
                </main>
            </div>
        </div>,
        document.body,
    );
};

export default ModalShell;
